import React, { FC, SVGAttributes, Suspense, lazy } from 'react';
import clsx from 'clsx';
import HTMLComment from 'react-html-comment';
import { IconNameTypes } from './IconNameTypes';
import { DisplayTypes, SizeTypes } from './IconTypes';
import { twMerge } from 'tailwind-merge';

export const NameTypes = Object.values(IconNameTypes);
type IconPropName = keyof typeof IconNameTypes;

type IconPropSize = keyof typeof SizeTypes;

type IconPropDisplay = keyof typeof DisplayTypes;

type IconComponentProps = SVGAttributes<SVGElement>;

const iconComponents: { [key: string]: React.LazyExoticComponent<FC<IconComponentProps>> } = {};

Object.values(IconNameTypes).forEach((name) => {
    iconComponents[name] = lazy(() => import(/* @vite-ignore */ `./Icons/${name}`));
});

interface IconProps {
    name: IconPropName;
    size?: IconPropSize;
    display?: IconPropDisplay;
    isAriaHidden?: boolean;
    className?: string;
}

const Icon: FC<IconProps> = ({ name, size = 'md', isAriaHidden = false, display = DisplayTypes.flex, className }) => {
    const classes = clsx(
        [size === 'free' && 'w-full h-auto'],
        [size === 'xs' && 'size-4'],
        [size === 'sm' && 'size-4.5'],
        [size === 'md' && 'size-6'],
        [size === 'lg' && 'size-8'],
        [size === 'xl' && 'size-10']
    );

    const DynamicIconComponent = iconComponents[name];

    if (!DynamicIconComponent) {
        return null;
    }

    return (
        <>
            <HTMLComment text={'@component: Icon - Start'} />
            <i role="img" aria-label={name} aria-hidden={isAriaHidden} className={`${display} fill-current !duration-0`}>
                <HTMLComment text={`Insert '${name}' SVG Icon here`} />
                <Suspense fallback={<div>Loading...</div>}>
                    <DynamicIconComponent className={twMerge(classes, display, className)} />
                </Suspense>
            </i>
            <HTMLComment text={'@component: Icon - End'} />
        </>
    );
};

export default Icon;
